import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTransferDestination } from "@/hooks/useTransferDestinations";
import Layout from "@/components/Layout";
import { TransferBookingForm } from "@/components/TransferBookingForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";

const TransferDestinationPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: destination, isLoading } = useTransferDestination(slug || "");

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">Cargando...</div>
        </div>
      </Layout>
    );
  }

  if (!destination) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Destino no encontrado</h1>
            <p className="text-muted-foreground mb-8">
              El destino que buscas no existe o no está disponible.
            </p>
            <Link to="/traslados">
              <Button>Ver todos los destinos</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{destination.meta_title || destination.title}</title>
        <meta
          name="description"
          content={destination.meta_description || destination.description || ""}
        />
        <meta name="keywords" content={destination.meta_keywords || ""} />
        <meta property="og:title" content={destination.meta_title || destination.title} />
        <meta
          property="og:description"
          content={destination.meta_description || destination.description || ""}
        />
        {destination.hero_image && <meta property="og:image" content={destination.hero_image} />}
      </Helmet>

      {/* Breadcrumbs */}
      <div className="bg-muted/30 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground">
              Inicio
            </Link>
            <span>/</span>
            <Link to="/traslados" className="hover:text-foreground">
              Transfers
            </Link>
            <span>/</span>
            <span className="text-foreground">{destination.name}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${destination.hero_image || '/placeholder.svg'})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {destination.title}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90">
            {destination.description}
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            {destination.estimated_time && (
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                <span className="font-semibold">⏱️ {destination.estimated_time}</span>
              </div>
            )}
            {destination.from_airport_price && (
              <div className="bg-primary/20 backdrop-blur-sm px-6 py-3 rounded-full border border-primary/40">
                <span className="font-semibold">💵 Desde ${destination.from_airport_price}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Booking Form Section - Full Width Below Hero */}
      <section className="relative -mt-20 z-20 px-4 pb-16">
        <div className="container mx-auto max-w-6xl">
          <TransferBookingForm preselectedDestination={destination.name} />
        </div>
      </section>

      {/* Highlights Section */}
      {destination.highlights && destination.highlights.length > 0 && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">¿Por qué elegirnos?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {destination.highlights.map((highlight, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{highlight}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content Section */}
      {destination.content && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto prose prose-lg">
              <div dangerouslySetInnerHTML={{ __html: destination.content }} />
            </div>
          </div>
        </section>
      )}

      {/* Popular Routes Section */}
      {destination.popular_routes && destination.popular_routes.length > 0 && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Rutas Populares</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {destination.popular_routes.map((route, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <MapPin className="h-6 w-6 text-primary" />
                      <Badge variant="outline">{route.time}</Badge>
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{route.name}</h3>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-2xl font-bold text-primary">${route.price}</span>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {destination.gallery_images && destination.gallery_images.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Galería</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {destination.gallery_images.map((img, index) => (
                <div key={index} className="aspect-square overflow-hidden rounded-lg">
                  <img
                    src={img}
                    alt={`${destination.name} ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default TransferDestinationPage;